// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — ProcessMapLevel2 (Concentric Tech Redesign)
// Group detail: KPIs + concentric orbital card + corp news + doc panel
// Same page structure as Level1
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PM } from '../../../../../config/processMapStyles';
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { useProcessesByGroup } from '../hooks/useProcessesByGroup';
import { useProcessNavigation } from '../hooks/useProcessNavigation';
import type { ProcessSummary } from '../types/process-map.types';
import { CommunicationCards } from './CommunicationCards';
import {
  IconDiagnostico,
  IconArquitectura,
  IconDespliegue,
  IconOperacion,
  IconMadurez,
  IconProcess,
  IconInformation,
  IconCompliance,
  IconTech,
  IconReputation,
  IconStructure,
  IconTrendUp,
  IconTrendDown,
  IconTrendStable,
} from './ProcessMapIcons';

// ─── Phase config ──────────────────────────────────────────

type IconFC = React.FC<{ size?: number; color?: string }>;

// (Phase config: PHASE_CONFIG / getPhaseConfigHUD defined below ConcentricCard)

// ─── Group icon from grupoNumero (reuse same map as Orbital) ──

const GROUP_ICON_MAP: Record<number, IconFC> = {
  3: IconStructure,
  4: IconProcess,
  5: IconInformation,
  6: IconCompliance,
  7: IconTech,
  8: IconReputation,
};

// ─── Trend icon helper ─────────────────────────────────────

function TrendIcon({ trend, size = 16 }: { trend: 'up' | 'down' | 'stable'; size?: number }) {
  const color = trend === 'up' ? PM.verdeOk : trend === 'down' ? PM.rojoError : PM.ambar;
  if (trend === 'up') return <IconTrendUp size={size} color={color} />;
  if (trend === 'down') return <IconTrendDown size={size} color={color} />;
  return <IconTrendStable size={size} color={color} />;
}

// ═══════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════

export function ProcessMapLevel2() {
  const { t } = useI18n();
  const { groupId } = useParams<{ groupId: string }>();
  const [searchParams] = useSearchParams();
  const groupNum = groupId ? parseInt(groupId, 10) : undefined;
  const tipologia = searchParams.get('tip') ?? undefined;
  const { group, procesos, loading, error } = useProcessesByGroup(groupNum, tipologia);
  const { goToLevel1, goToLevel3 } = useProcessNavigation();

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LoadingSpinner text={t.processMap.cargandoGrupo} />
        </div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              color: PM.rojoError,
              fontSize: 13,
              backgroundColor: '#FEF2F2',
              padding: '12px 20px',
              borderRadius: 10,
              border: '1px solid #FECACA',
            }}
          >
            {error ?? t.processMap.grupoNoEncontrado}
          </div>
          <button
            onClick={goToLevel1}
            style={{
              fontSize: 13,
              color: PM.azul2,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t.processMap.volverAlMapa}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left column: KPIs + Concentric Card + Corp News */}
        <div
          style={{
            flex: 1,
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {/* KPI Strip */}
          <div style={{ flexShrink: 0 }}>
            <KpiStripLevel2 procesos={procesos} />
          </div>

          {/* Concentric orbital card — fills remaining space */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <ConcentricCard
              group={group}
              procesos={procesos}
              onProcessClick={goToLevel3}
              onBack={goToLevel1}
            />
          </div>

          {/* Corporate Communication card */}
          <div style={{ flexShrink: 0 }}>
            <CommunicationCards />
          </div>
        </div>

        {/* Right column: Gestión Documental panel */}
        <div
          style={{
            width: 340,
            minWidth: 340,
            flexShrink: 0,
            margin: '20px 24px 20px 0',
            backgroundColor: PM.surface,
            borderRadius: PM.card.borderRadius,
            border: PM.card.border,
            boxShadow: PM.card.shadow,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <GestionDocumentalPanel />
        </div>
      </div>
    </div>
  );
}

// ─── KPI Strip (real data from group) ──────────────────────

interface KpiItem {
  titulo: string;
  valor: string;
  tendencia: 'up' | 'down' | 'stable';
  descripcion: string;
}

function KpiStripLevel2({ procesos }: { procesos: ProcessSummary[] }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const totalProcesos = procesos.length;
  const totalDocs = procesos.reduce((s, p) => s + p.documentCount, 0);
  const conFase = procesos.filter((p) => p.fase).length;
  const conResponsable = procesos.filter((p) => p.responsable).length;
  const fasesUnicas = new Set(procesos.map((p) => p.fase).filter(Boolean));
  const docsPorProceso = totalProcesos > 0 ? (totalDocs / totalProcesos).toFixed(1) : '0';
  const conFasePct = totalProcesos > 0 ? Math.round((conFase / totalProcesos) * 100) : 0;

  const { t } = useI18n();
  const kpis: KpiItem[] = [
    {
      titulo: t.processMap.kpiProcesosDelGrupo,
      valor: String(totalProcesos),
      tendencia: 'stable',
      descripcion: `${tipLabel(procesos, t)}`,
    },
    {
      titulo: t.processMap.kpiDocumentosVinculados,
      valor: String(totalDocs),
      tendencia: totalDocs > 0 ? 'up' : 'stable',
      descripcion: `${totalProcesos} ${t.process.procesos.toLowerCase()}`,
    },
    {
      titulo: t.processMap.kpiConFaseAsignada,
      valor: `${conFase}`,
      tendencia: conFasePct >= 80 ? 'up' : conFasePct >= 50 ? 'stable' : 'down',
      descripcion: `${conFasePct}% ${t.processMap.delGrupo}`,
    },
    {
      titulo: t.processMap.kpiConResponsable,
      valor: String(conResponsable),
      tendencia: conResponsable === totalProcesos ? 'up' : 'stable',
      descripcion: t.processMap.deProcesos(totalProcesos),
    },
    {
      titulo: t.processMap.kpiFasesCubiertas,
      valor: `${fasesUnicas.size}/5`,
      tendencia: fasesUnicas.size >= 4 ? 'up' : fasesUnicas.size >= 2 ? 'stable' : 'down',
      descripcion: t.processMap.delCicloDeVida,
    },
    {
      titulo: t.processMap.kpiDocsPorProceso,
      valor: docsPorProceso,
      tendencia: 'stable',
      descripcion: t.processMap.promedioDelGrupo,
    },
    {
      titulo: t.processMap.kpiConformidad,
      valor: '\u2014',
      tendencia: 'stable',
      descripcion: t.processMap.pendienteDeMedicion,
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
      {kpis.map((kpi, idx) => (
        <div
          key={idx}
          onMouseEnter={() => setHoveredIdx(idx)}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{
            flex: '1 1 160px',
            minWidth: 140,
            backgroundColor: PM.surface,
            borderRadius: PM.card.borderRadius,
            border: PM.card.border,
            boxShadow: hoveredIdx === idx ? PM.card.shadowHover : PM.card.shadow,
            transform: hoveredIdx === idx ? 'translateY(-2px)' : 'none',
            transition: PM.card.transition,
            padding: '16px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ fontSize: 11, color: PM.textSec, fontWeight: 500 }}>{kpi.titulo}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 28, fontWeight: 800, color: PM.text, lineHeight: 1 }}>
              {kpi.valor}
            </span>
            <TrendIcon trend={kpi.tendencia} />
          </div>
          <div style={{ fontSize: 10, color: PM.textMut }}>{kpi.descripcion}</div>
        </div>
      ))}
    </div>
  );
}

function tipLabel(procesos: ProcessSummary[], t: any): string {
  if (procesos.length === 0) return t.processMap.sinProcesos;
  const fases = procesos.filter((p) => p.fase).map((p) => p.fase!);
  if (fases.length === 0) return t.processMap.sinFasesAsignadas;
  const counts: Record<string, number> = {};
  for (const f of fases) counts[f] = (counts[f] ?? 0) + 1;
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return top ? t.processMap.mayoriaEn(top[0]) : '';
}

// ─── Phase config for concentric HUD ─────────────────────

const PHASE_CONFIG: Record<
  string,
  { color: string; Icon: React.FC<{ size?: number; color?: string }> }
> = {
  DIAGNOSTICO: { color: '#F59E0B', Icon: IconDiagnostico },
  ARQUITECTURA: { color: '#2F5496', Icon: IconArquitectura },
  DESPLIEGUE: { color: '#1A94BF', Icon: IconDespliegue },
  OPERACION: { color: '#16A34A', Icon: IconOperacion },
  MADUREZ: { color: '#33BCE1', Icon: IconMadurez },
};
const DEFAULT_PHASE_CFG = { color: '#64748B', Icon: IconProcess };

function getPhaseConfigHUD(fase: string | undefined): {
  color: string;
  Icon: React.FC<{ size?: number; color?: string }>;
} {
  if (!fase) return DEFAULT_PHASE_CFG;
  const key = fase
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return PHASE_CONFIG[key] ?? DEFAULT_PHASE_CFG;
}

// ─── Concentric Card (HUD Tech Scanner) ──────────────────

function ConcentricCard({
  group,
  procesos,
  onProcessClick,
  onBack,
}: {
  group: { grupoNumero: number; grupoNombre: string; tipologia: string };
  procesos: ProcessSummary[];
  onProcessClick: (id: string) => void;
  onBack: () => void;
}) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(800);
  const [ch, setCh] = useState(500);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCw(entry.contentRect.width);
        setCh(entry.contentRect.height);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const scale = Math.min(cw, ch) / 520;
  const cx = cw / 2;
  const cy = ch * 0.58;

  // Group icon
  const GroupIcon = GROUP_ICON_MAP[group.grupoNumero] ?? IconProcess;

  // Ring radii
  const r1 = 80 * scale;
  const r2 = 130 * scale;
  const r3 = 180 * scale;
  const r4 = 230 * scale;

  // Process node placement
  const nodeRadius = 260 * scale;
  const nodeR = 28 * scale;
  const count = procesos.length;
  const startAngle = 160;
  const endAngle = 20;
  const angleStep = count > 1 ? (endAngle - startAngle) / (count - 1) : 0;

  // Scan marker dots (decorative)
  const scanDots = [
    { ring: r1 + 10, angle: 45, r: 2.5 },
    { ring: r2 - 5, angle: 135, r: 2 },
    { ring: r2 + 15, angle: 280, r: 3 },
    { ring: r3 - 10, angle: 55, r: 2 },
    { ring: r3 + 8, angle: 210, r: 2.5 },
    { ring: r4 - 12, angle: 330, r: 2 },
  ];

  // Tipologia label
  const brandLabel =
    group.tipologia === 'PE'
      ? `VALOR\u00B7IA Process \u2014 ${t.processMap.estrategico}`
      : group.tipologia === 'PO'
        ? `VALOR\u00B7IA Process \u2014 ${t.processMap.operativo}`
        : `VALOR\u00B7IA Process \u2014 ${t.processMap.soporteCorto}`;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        height: '100%',
        minHeight: 350,
        background: '#FFFFFF',
        overflow: 'hidden',
        borderRadius: PM.card.borderRadius,
        border: PM.card.border,
        boxShadow: PM.card.shadow,
      }}
    >
      {/* Back button — pill style */}
      <button
        onClick={onBack}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#F7F8FA';
          e.currentTarget.style.boxShadow = '0 3px 10px rgba(31,56,100,0.1)';
          e.currentTarget.style.transform = 'translateX(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
          e.currentTarget.style.boxShadow = '0 1px 4px rgba(31,56,100,0.06)';
          e.currentTarget.style.transform = 'none';
        }}
        style={{
          position: 'absolute',
          top: 14,
          left: 16,
          zIndex: 10,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 18px',
          borderRadius: 24,
          border: '1px solid #E5E7EB',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(4px)',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500,
          color: '#1F3864',
          fontFamily: 'inherit',
          transition: 'all 0.2s ease',
          boxShadow: '0 1px 4px rgba(31,56,100,0.06)',
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#F0F4F8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1F3864"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </span>
        {t.processMap.mapaDeProcesos}
      </button>

      {/* SVG HUD Scanner */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <defs>
          <style>{`
            @keyframes scanR { to { stroke-dashoffset: -60; } }
            @keyframes scanL { to { stroke-dashoffset: 60; } }
            @keyframes pulse { 0%, 100% { opacity: 0.08; } 50% { opacity: 0.30; } }
          `}</style>
        </defs>

        {/* Subtle background circle */}
        <circle cx={cx} cy={cy} r={100 * scale} fill="rgba(31,56,100,0.03)" />

        {/* "VA" watermark text */}
        <text
          x={cx}
          y={cy + 14 * scale}
          textAnchor="middle"
          fontSize={40 * scale}
          fontWeight={300}
          fill="#1F3864"
          opacity={0.04}
          letterSpacing={8}
          style={{ pointerEvents: 'none' }}
        >
          VA
        </text>

        {/* Imagotipo watermark behind rings */}
        <image
          href="/img/Imagotipo-Color.png"
          x={cx - 90 * scale}
          y={cy - 90 * scale}
          width={180 * scale}
          height={180 * scale}
          opacity={0.06}
          style={{ filter: 'blur(2px)', pointerEvents: 'none' }}
        />

        {/* Animated concentric rings (will-change for GPU compositing) */}
        <circle
          cx={cx}
          cy={cy}
          r={r1}
          fill="none"
          stroke="#33BCE1"
          strokeWidth={1.2}
          strokeDasharray="4 6"
          opacity={0.1}
          style={{ animation: 'scanR 6s linear infinite', willChange: 'stroke-dashoffset' }}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r2}
          fill="none"
          stroke="#1A94BF"
          strokeWidth={0.8}
          strokeDasharray="12 8"
          opacity={0.07}
          style={{ animation: 'scanL 10s linear infinite', willChange: 'stroke-dashoffset' }}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r3}
          fill="none"
          stroke="#33BCE1"
          strokeWidth={0.6}
          strokeDasharray="2 4"
          opacity={0.05}
          style={{ animation: 'scanR 14s linear infinite', willChange: 'stroke-dashoffset' }}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r4}
          fill="none"
          stroke="#1A94BF"
          strokeWidth={0.5}
          strokeDasharray="20 10 4 10"
          opacity={0.03}
          style={{ animation: 'scanL 18s linear infinite', willChange: 'stroke-dashoffset' }}
        />

        {/* Scan marker dots with pulse */}
        {scanDots.map((d, i) => {
          const rad = (d.angle * Math.PI) / 180;
          const dx = cx + d.ring * Math.cos(rad);
          const dy = cy - d.ring * Math.sin(rad);
          return (
            <circle
              key={`dot-${i}`}
              cx={dx}
              cy={dy}
              r={d.r * scale}
              fill="#33BCE1"
              style={{ animation: `pulse ${2 + i * 0.7}s ease-in-out infinite` }}
            />
          );
        })}

        {/* Center group icon circle */}
        <circle cx={cx} cy={cy} r={30 * scale} fill="#0E243E" stroke="#33BCE1" strokeWidth={1} />
        {/* Outer decorative ring around center */}
        <circle
          cx={cx}
          cy={cy}
          r={31 * scale}
          fill="none"
          stroke="#33BCE1"
          strokeWidth={0.3}
          opacity={0.3}
        />

        {/* Radial connector lines from nodes to center */}
        {procesos.map((p, idx) => {
          const angle = count > 1 ? startAngle + idx * angleStep : (startAngle + endAngle) / 2;
          const rad = (angle * Math.PI) / 180;
          const nx = cx + nodeRadius * Math.cos(rad);
          const ny = cy - nodeRadius * Math.sin(rad);
          const { color } = getPhaseConfigHUD(p.fase);
          const isHov = hoveredIdx === idx;
          // Intersection point on innermost ring
          const ix = cx + r1 * Math.cos(rad);
          const iy = cy - r1 * Math.sin(rad);
          return (
            <g key={`line-${idx}`}>
              <line
                x1={cx}
                y1={cy}
                x2={nx}
                y2={ny}
                stroke={color}
                strokeWidth={0.8}
                strokeDasharray="3 3"
                opacity={isHov ? 0.4 : 0.12}
                style={{ transition: 'opacity 0.25s' }}
              />
              <circle cx={ix} cy={iy} r={2.5 * scale} fill={color} opacity={0.25} />
            </g>
          );
        })}

        {/* Bottom tech bars */}
        <line
          x1={cx - 180 * scale}
          y1={cy + r4 + 30 * scale}
          x2={cx + 180 * scale}
          y2={cy + r4 + 30 * scale}
          stroke="#33BCE1"
          strokeWidth={0.3}
          opacity={0.06}
        />
        <rect
          x={cx - 160 * scale}
          y={cy + r4 + 36 * scale}
          width={60 * scale}
          height={3 * scale}
          rx={1.5}
          fill="#33BCE1"
          opacity={0.04}
        />
        <rect
          x={cx - 80 * scale}
          y={cy + r4 + 36 * scale}
          width={40 * scale}
          height={4 * scale}
          rx={2}
          fill="#33BCE1"
          opacity={0.03}
        />
        <rect
          x={cx - 20 * scale}
          y={cy + r4 + 36 * scale}
          width={80 * scale}
          height={3 * scale}
          rx={1.5}
          fill="#33BCE1"
          opacity={0.04}
        />
        <rect
          x={cx + 80 * scale}
          y={cy + r4 + 36 * scale}
          width={50 * scale}
          height={4 * scale}
          rx={2}
          fill="#33BCE1"
          opacity={0.03}
        />
      </svg>

      {/* Center group icon (HTML overlay for crisp icon) */}
      <div
        style={{
          position: 'absolute',
          left: cx - 30 * scale,
          top: cy - 30 * scale,
          width: 60 * scale,
          height: 60 * scale,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3,
          pointerEvents: 'none',
        }}
      >
        <GroupIcon size={24 * scale} color="#33BCE1" />
      </div>

      {/* Group name + brand label below center */}
      <div
        style={{
          position: 'absolute',
          left: cx - 120,
          top: cy + 34 * scale,
          width: 240,
          textAlign: 'center',
          zIndex: 3,
          pointerEvents: 'none',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1F3864', lineHeight: 1.3 }}>
          {group.grupoNombre}
        </div>
        <div
          style={{
            fontSize: 10,
            color: '#33BCE1',
            marginTop: 2,
            fontStyle: 'italic',
            opacity: 0.7,
          }}
        >
          {brandLabel}
        </div>
      </div>

      {/* Process nodes on upper semicircle */}
      {procesos.map((p, idx) => {
        const angle = count > 1 ? startAngle + idx * angleStep : (startAngle + endAngle) / 2;
        const rad = (angle * Math.PI) / 180;
        const nx = cx + nodeRadius * Math.cos(rad);
        const ny = cy - nodeRadius * Math.sin(rad);
        return (
          <ProcessNodeHUD
            key={p.id}
            proceso={p}
            x={nx}
            y={ny}
            nodeR={nodeR}
            scale={scale}
            hovered={hoveredIdx === idx}
            onHover={(h) => setHoveredIdx(h ? idx : null)}
            onClick={() => onProcessClick(p.id)}
          />
        );
      })}

      {/* Empty state */}
      {procesos.length === 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: PM.textMut,
            fontSize: 14,
            zIndex: 5,
          }}
        >
          {t.processMap.sinProcesosGrupo}
        </div>
      )}
    </div>
  );
}

// ─── Process Node HUD (on concentric arc) ─────────────────

function ProcessNodeHUD({
  proceso,
  x,
  y,
  nodeR,
  scale,
  hovered,
  onHover,
  onClick,
}: {
  proceso: ProcessSummary;
  x: number;
  y: number;
  nodeR: number;
  scale: number;
  hovered: boolean;
  onHover: (h: boolean) => void;
  onClick: () => void;
}) {
  const { color, Icon } = getPhaseConfigHUD(proceso.fase);
  const outerR = nodeR + 4 * scale;
  const sz = nodeR * 2;

  return (
    <>
      {/* Outer HUD ring (SVG-positioned via absolute div) */}
      <svg
        style={{
          position: 'absolute',
          left: x - outerR - 2,
          top: y - outerR - 2,
          width: (outerR + 2) * 2,
          height: (outerR + 2) * 2,
          pointerEvents: 'none',
          zIndex: 3,
        }}
      >
        <circle
          cx={outerR + 2}
          cy={outerR + 2}
          r={outerR}
          fill="none"
          stroke={color}
          strokeWidth={0.3}
          opacity={0.4}
        />
      </svg>

      {/* Main circle */}
      <div
        style={{
          position: 'absolute',
          left: x - nodeR,
          top: y - nodeR,
          width: sz,
          height: sz,
          borderRadius: '50%',
          background: '#FFFFFF',
          border: `1.2px solid ${color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 4,
          transform: hovered ? 'scale(1.12)' : 'scale(1)',
          boxShadow: hovered ? `0 0 16px ${color}40` : 'none',
          transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
        onClick={onClick}
        onMouseEnter={() => onHover(true)}
        onMouseLeave={() => onHover(false)}
      >
        <Icon size={Math.max(16, 20 * scale)} color={color} />
      </div>

      {/* Label below: process name */}
      <div
        style={{
          position: 'absolute',
          left: x - 55,
          top: y + nodeR + 6 * scale,
          width: 110,
          textAlign: 'center',
          zIndex: 4,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#1F3864',
            lineHeight: 1.25,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {proceso.nombre}
        </div>
        <div
          style={{
            fontSize: 9,
            color: 'rgba(31,56,100,0.45)',
            marginTop: 1,
            lineHeight: 1.2,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {proceso.codigo ?? ''}
        </div>
      </div>

      {/* Phase pill */}
      {proceso.fase && (
        <div
          style={{
            position: 'absolute',
            left: x - 40,
            top: y + nodeR + 32 * scale,
            width: 80,
            textAlign: 'center',
            zIndex: 4,
            pointerEvents: 'none',
          }}
        >
          <svg width={80} height={18}>
            <rect
              x={0}
              y={0}
              width={80}
              height={18}
              rx={7}
              fill={`${color}1F`}
              stroke={color}
              strokeWidth={0.5}
            />
            <text
              x={40}
              y={12.5}
              textAnchor="middle"
              fontSize={8}
              fontWeight={700}
              fill={color}
              fontFamily="inherit"
            >
              {proceso.fase}
            </text>
          </svg>
        </div>
      )}
    </>
  );
}

// ─── Right Panel: Gestión Documental ───────────────────────

function GestionDocumentalPanel() {
  const { t } = useI18n();
  return (
    <>
      {/* Header */}
      <div
        style={{
          padding: '20px 18px 16px',
          background: PM.gradientDark,
          color: '#fff',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700 }}>{t.processMap.gestionDocumentalTitulo}</div>
        <div
          style={{
            fontSize: 10,
            color: 'rgba(255,255,255,0.55)',
            marginTop: 4,
            fontStyle: 'italic',
          }}
        >
          VALOR·IA Information
        </div>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          padding: '16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          overflowY: 'auto',
        }}
      >
        <DocLinkCard
          icon={<IconInformation size={20} color={PM.azul2} />}
          title={t.processMap.gestionDocDepartamental}
          description={t.processMap.gestionDocDepartamentalDesc}
          url={null}
        />
        <DocLinkCard
          icon={<IconInformation size={20} color={PM.azulClaro} />}
          title={t.processMap.gestionDocProyectos}
          description={t.processMap.gestionDocProyectosDesc}
          url={null}
        />

        {/* Separador */}
        <div
          style={{
            height: 1,
            background: 'linear-gradient(90deg, transparent, #E5E7EB, transparent)',
            margin: '8px 0',
          }}
        />

        {/* Equipo del Grupo */}
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#1F3864',
            letterSpacing: 0.5,
            marginBottom: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div style={{ width: 3, height: 14, borderRadius: 2, background: '#1F3864' }} />
          {t.processMap.equipoDelGrupo}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 10,
              background: '#F7F8FA',
              border: '1px solid #F0F0F0',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: '#1F3864',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              DR
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#1E293B' }}>
                {t.processMap.directorResponsableLabel}
              </div>
              <div style={{ fontSize: 10, color: '#94A3B8' }}>{t.processMap.dirGralLabel}</div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 10,
              background: '#F7F8FA',
              border: '1px solid #F0F0F0',
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: '#33BCE1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              RP
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#1E293B' }}>
                {t.processMap.representanteDelProceso}
              </div>
              <div style={{ fontSize: 10, color: '#94A3B8' }}>
                {t.processMap.pendienteDeAsignar}
              </div>
            </div>
          </div>
        </div>

        {/* Separador */}
        <div
          style={{
            height: 1,
            background: 'linear-gradient(90deg, transparent, #E5E7EB, transparent)',
            margin: '8px 0',
          }}
        />

        {/* Actividad Reciente */}
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#1A94BF',
            letterSpacing: 0.5,
            marginBottom: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div style={{ width: 3, height: 14, borderRadius: 2, background: '#1A94BF' }} />
          {t.processMap.actividadReciente}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {[
            {
              texto: t.processMap.actividad.fichaActualizada,
              fecha: t.processMap.actividad.hace2Dias,
              color: '#16A34A',
            },
            {
              texto: t.processMap.actividad.flujogramaEnviado,
              fecha: t.processMap.actividad.hace3Dias,
              color: '#2F5496',
            },
            {
              texto: t.processMap.actividad.nuevoDocVinculado,
              fecha: t.processMap.actividad.hace5Dias,
              color: '#33BCE1',
            },
            {
              texto: t.processMap.actividad.indicadorRevisado,
              fecha: t.processMap.actividad.hace1Semana,
              color: '#F59E0B',
            },
            {
              texto: t.processMap.actividad.procesoAsignado,
              fecha: t.processMap.actividad.hace2Semanas,
              color: '#1F3864',
            },
          ].map((item, idx, arr) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                gap: 12,
                padding: '8px 0',
                borderBottom: idx < arr.length - 1 ? '1px solid #F5F5F5' : 'none',
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: item.color,
                  marginTop: 4,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#1E293B', lineHeight: 1.4 }}>{item.texto}</div>
                <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 2 }}>{item.fecha}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '14px 18px',
          borderTop: `1px solid ${PM.border}`,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 10, color: PM.textMut, fontStyle: 'italic' }}>
          {t.processMap.servicioInformation}
        </div>
      </div>
    </>
  );
}

function DocLinkCard({
  icon,
  title,
  description,
  url,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  url: string | null;
}) {
  const { t } = useI18n();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '14px 16px',
        borderRadius: 10,
        border: PM.card.border,
        backgroundColor: hovered ? PM.acentoSuave : PM.bg,
        transition: 'background-color 0.2s',
        cursor: url ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            backgroundColor: `${PM.azul2}10`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: PM.text, lineHeight: 1.3 }}>
            {title}
          </div>
          <div style={{ fontSize: 11, color: PM.textMut, marginTop: 4, lineHeight: 1.4 }}>
            {description}
          </div>
          <span
            style={{
              display: 'inline-block',
              marginTop: 8,
              fontSize: 9,
              fontWeight: 600,
              color: url ? PM.verdeOk : PM.ambar,
              backgroundColor: url ? `${PM.verdeOk}12` : `${PM.ambar}12`,
              padding: '2px 8px',
              borderRadius: 6,
              border: `1px solid ${url ? PM.verdeOk : PM.ambar}30`,
            }}
          >
            {url ? t.processMap.disponible : t.processMap.pendienteDeConfigurar}
          </span>
        </div>
      </div>
    </div>
  );
}
